import { useState, useEffect, useMemo, useCallback, Fragment } from 'react';
import type { ChangeEvent, ReactNode } from 'react';
import type { ContextMessage } from '../../../shared/types/context';
import { api } from '../../lib/api';
import { computeLineDiff, type DiffEntry } from '../../lib/diff';
import { useSelectionStore, type DiffLayout } from '../../state/selectionStore';
import { usePanelContext } from '../layout/PanelContext';
import { TextContentViewer } from '../TextContentViewer';
import { ToggleButton } from '../ToggleButton';
import { bus } from '../../App';

type ViewMode = DiffLayout;

interface DiffSourceOption {
  id: string;
  label: string;
  type: 'current' | 'version';
  versionId?: number;
}

type DiffCellKind = 'context' | 'added' | 'removed' | 'blank';

interface DiffCell {
  kind: DiffCellKind;
  lineNumber: number | null;
  text: string;
}

interface BaseRow {
  key: string;
  left: DiffCell;
  right: DiffCell;
}

interface ContextBlock {
  id: string;
  start: number;
  end: number;
}

interface RangeInfo {
  start: number | null;
  end: number | null;
}

interface CollapseInfo {
  blockId: string;
  hiddenCount: number;
  beforeRange: RangeInfo;
  afterRange: RangeInfo;
}

type VisibleRow =
  | { type: 'line'; key: string; row: BaseRow }
  | { type: 'collapse'; key: string; info: CollapseInfo };

interface DiffStats {
  added: number;
  removed: number;
}

interface DiffFileData {
  path: string;
  entries: DiffEntry[];
  rows: BaseRow[];
  blocks: ContextBlock[];
  stats: DiffStats;
  beforeExists: boolean;
  afterExists: boolean;
}

interface DiffSection {
  file: DiffFileData;
  rows: VisibleRow[];
}

interface DiffTreeNode {
  key: string;
  name: string;
  isFolder: boolean;
  children: DiffTreeNode[];
  added: number;
  removed: number;
}

interface DiffContentProps {
  sections: DiffSection[];
  viewMode: ViewMode;
  onToggleBlock: (blockId: string) => void;
}

const COLLAPSE_THRESHOLD = 12;
const CONTEXT_MARGIN = 3;
const MAX_NAV_DEPTH_CLASS = 8;

interface InlineSegment {
  key: string;
  kind: DiffCellKind;
  before: number | null;
  after: number | null;
  marker: string;
  text: string;
}

export function DiffPanel() {
  const filePath = useSelectionStore((state) => state.file_path);
  const selectedVersionId = useSelectionStore((state) => state.version_id);
  const selectedMessageId = useSelectionStore((state) => state.message_id);
  const diffFilePathValue = useSelectionStore((state) => state.diff_file_path);
  const diffBeforeVersionIdValue = useSelectionStore((state) => state.diff_before_version_id);
  const diffAfterVersionIdValue = useSelectionStore((state) => state.diff_after_version_id);
  const diffLayoutValue = useSelectionStore((state) => state.diff_layout);
  const select = useSelectionStore((state) => state.set);
  const { panelId, setTitle } = usePanelContext();

  const [hostVersionId, setHostVersionId] = useState<number | null>(null);
  const [messageVersionId, setMessageVersionId] = useState<number | null>(null);
  const [versions, setVersions] = useState<Array<{ id: number; created_at: number; fileCount: number }>>([]);
  const [beforeOptionId, setBeforeOptionId] = useState<string | null>(null);
  const [afterOptionId, setAfterOptionId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [diffFiles, setDiffFiles] = useState<DiffFileData[]>([]);
  const [expandedBlocks, setExpandedBlocks] = useState<Set<string>>(new Set());
  const [totalStats, setTotalStats] = useState<DiffStats>({ added: 0, removed: 0 });
  const [currentSourcesRevision, setCurrentSourcesRevision] = useState(0);
  const [showFileList, setShowFileList] = useState(true);

  const diffBeforeVersionId = diffBeforeVersionIdValue ?? null;
  const diffAfterVersionId = diffAfterVersionIdValue ?? null;
  const diffLayout = diffLayoutValue ?? 'inline';
  const viewMode: ViewMode = diffLayout;

  useEffect(() => {
    if (!setTitle) {
      return;
    }
    if (!filePath) {
      setTitle(panelId, 'Diff');
      return;
    }
    setTitle(panelId, `Diff · ${filePath}`);
  }, [filePath, panelId, setTitle]);

  useEffect(() => {
    let cancelled = false;

    const loadHostVersion = async () => {
      try {
        const value = await api().services.meta.get('host_version_id');
        if (cancelled) {
          return;
        }
        if (typeof value === 'number') {
          setHostVersionId(Number.isFinite(value) ? value : null);
          return;
        }
        if (typeof value === 'string') {
          const parsed = Number(value);
          setHostVersionId(Number.isFinite(parsed) ? parsed : null);
          return;
        }
        setHostVersionId(null);
      } catch (err) {
        if (!cancelled) {
          console.error('[DiffPanel] Failed to load host_version_id:', err);
        }
      }
    };

    loadHostVersion();

    const unsubscribe = bus.on('meta/set', (_event, payload) => {
      if (payload?.key !== 'host_version_id') {
        return;
      }
      const raw = payload.value;
      if (typeof raw === 'number') {
        setHostVersionId(Number.isFinite(raw) ? raw : null);
        return;
      }
      if (typeof raw === 'string') {
        const parsed = Number(raw);
        setHostVersionId(Number.isFinite(parsed) ? parsed : null);
        return;
      }
      setHostVersionId(null);
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadVersions = async () => {
      try {
        const list = await api().services.files.getVersions();
        if (!cancelled) {
          setVersions(list ?? []);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[DiffPanel] Failed to load versions:', err);
        }
      }
    };

    loadVersions();

    const handleRefresh = async () => {
      try {
        const list = await api().services.files.getVersions();
        setVersions(list ?? []);
      } catch (err) {
        console.error('[DiffPanel] Failed to refresh versions:', err);
      }
    };

    const unsubscribeCreated = bus.on('version/created', handleRefresh);
    const unsubscribeUpdated = bus.on('version/updated', handleRefresh);

    return () => {
      cancelled = true;
      unsubscribeCreated();
      unsubscribeUpdated();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;

    if (!selectedMessageId) {
      setMessageVersionId(null);
      return;
    }

    const loadMessage = async () => {
      try {
        const message = (await api().services.context.getMessage(selectedMessageId)) as ContextMessage | null;
        if (!cancelled) {
          setMessageVersionId(message?.version_id ?? null);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[DiffPanel] Failed to load message for version:', err);
          setMessageVersionId(null);
        }
      }
    };

    loadMessage();

    return () => {
      cancelled = true;
    };
  }, [selectedMessageId]);

  const options = useMemo(() => {
    const list: DiffSourceOption[] = [];

    list.push({ id: 'current', label: 'files', type: 'current' });

    if (hostVersionId !== null) {
      list.push({
        id: 'host',
        label: `Hosted`,
        type: 'version',
        versionId: hostVersionId,
      });
    }

    if (selectedVersionId !== null) {
      list.push({
        id: 'selected',
        label: `Selected`,
        type: 'version',
        versionId: selectedVersionId,
      });
    }

    if (messageVersionId !== null) {
      list.push({
        id: 'message',
        label: `Message`,
        type: 'version',
        versionId: messageVersionId,
      });
    }

    const seen = new Set<number>();
    for (const option of list) {
      if (option.type === 'version' && option.versionId !== undefined && option.versionId !== null) {
        seen.add(option.versionId);
      }
    }

    versions.forEach((version) => {
      if (seen.has(version.id)) {
        return;
      }
      list.push({
        id: `version-${version.id}`,
        label: `Version #${version.id}`,
        type: 'version',
        versionId: version.id,
      });
    });

    return list;
  }, [hostVersionId, messageVersionId, selectedVersionId, versions]);

  const optionMap = useMemo(() => {
    const map = new Map<string, DiffSourceOption>();
    options.forEach((option) => {
      map.set(option.id, option);
    });
    return map;
  }, [options]);

  const beforeOption = beforeOptionId ? optionMap.get(beforeOptionId) : undefined;
  const afterOption = afterOptionId ? optionMap.get(afterOptionId) : undefined;
  const usesCurrentSources = (beforeOption?.type === 'current') || (afterOption?.type === 'current');

  useEffect(() => {
    if (!usesCurrentSources) {
      return;
    }

    const handleCurrentSourceChange = () => {
      setCurrentSourcesRevision((prev) => prev + 1);
    };

    const unsubscribes = [
      bus.on('file/create', handleCurrentSourceChange),
      bus.on('file/update', handleCurrentSourceChange),
      bus.on('file/delete', handleCurrentSourceChange),
      bus.on('files/checkout', handleCurrentSourceChange),
      bus.on('files/reset', handleCurrentSourceChange),
      bus.on('files/version', handleCurrentSourceChange),
    ];

    return () => {
      unsubscribes.forEach((unsubscribe) => {
        unsubscribe();
      });
    };
  }, [usesCurrentSources]);

  useEffect(() => {
    if (!options.length) {
      setBeforeOptionId(null);
      setAfterOptionId(null);
      if (diffBeforeVersionId !== null) {
        select({ diff_before_version_id: null });
      }
      if (diffAfterVersionId !== null) {
        select({ diff_after_version_id: null });
      }
      return;
    }

    const findOptionIdByVersion = (versionId: number) => {
      const match = options.find((option) => option.versionId === versionId);
      return match?.id ?? null;
    };

    const currentOptionId = options.find((option) => option.id === 'current')?.id ?? null;
    const selectedOptionId = options.find((option) => option.id === 'selected')?.id ?? null;
    const firstOptionId = options[0]?.id ?? null;
    const baseCurrentId = currentOptionId ?? firstOptionId;

    const previousVersion = versions.length > 1 ? versions[1] : versions[0];
    const previousVersionOptionId = previousVersion ? findOptionIdByVersion(previousVersion.id) : null;

    const storedAfterOptionId = diffAfterVersionId !== null ? findOptionIdByVersion(diffAfterVersionId) : null;
    const storedBeforeOptionId = diffBeforeVersionId !== null ? findOptionIdByVersion(diffBeforeVersionId) : null;
    const validAfterOptionId = afterOptionId && optionMap.has(afterOptionId) ? afterOptionId : null;
    const validBeforeOptionId = beforeOptionId && optionMap.has(beforeOptionId) ? beforeOptionId : null;

    const resolvedAfterId = validAfterOptionId ?? storedAfterOptionId ?? baseCurrentId;

    const defaultBeforeCandidate = selectedOptionId ?? previousVersionOptionId ?? baseCurrentId ?? resolvedAfterId;
    const resolvedBeforeId = validBeforeOptionId ?? storedBeforeOptionId ?? defaultBeforeCandidate;

    const normalizedAfterId = resolvedAfterId ?? baseCurrentId;
    const normalizedBeforeId = resolvedBeforeId ?? baseCurrentId;

    if (beforeOptionId !== normalizedBeforeId) {
      setBeforeOptionId(normalizedBeforeId);
    }
    if (afterOptionId !== normalizedAfterId) {
      setAfterOptionId(normalizedAfterId);
    }

    const beforeOption = normalizedBeforeId ? optionMap.get(normalizedBeforeId) : undefined;
    const afterOption = normalizedAfterId ? optionMap.get(normalizedAfterId) : undefined;

    const beforeVersionId = beforeOption?.type === 'version' ? beforeOption.versionId ?? null : null;
    const afterVersionId = afterOption?.type === 'version' ? afterOption.versionId ?? null : null;

    if (diffBeforeVersionId !== beforeVersionId) {
      select({ diff_before_version_id: beforeVersionId ?? null });
    }
    if (diffAfterVersionId !== afterVersionId) {
      select({ diff_after_version_id: afterVersionId ?? null });
    }
  }, [
    options,
    optionMap,
    versions,
    diffBeforeVersionId,
    diffAfterVersionId,
    beforeOptionId,
    afterOptionId,
    select,
  ]);

  useEffect(() => {
    let cancelled = false;

    if (!beforeOptionId || !afterOptionId) {
      setDiffFiles([]);
      setTotalStats({ added: 0, removed: 0 });
      setExpandedBlocks(new Set());
      setLoading(false);
      return;
    }

    const beforeOption = optionMap.get(beforeOptionId);
    const afterOption = optionMap.get(afterOptionId);
    if (!beforeOption || !afterOption) {
      return;
    }

    const loadDiff = async () => {
      setLoading(true);
      setError(null);
      try {
        const [beforePaths, afterPaths] = await Promise.all([
          fetchFileList(beforeOption),
          fetchFileList(afterOption),
        ]);

        if (cancelled) {
          return;
        }

        const pathSet = new Set<string>([...beforePaths, ...afterPaths]);
        const sortedPaths = Array.from(pathSet).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

        const fileSummaries: DiffFileData[] = [];
        let addedTotal = 0;
        let removedTotal = 0;

        for (const path of sortedPaths) {
          const [beforeContentRaw, afterContentRaw] = await Promise.all([
            fetchContent(beforeOption, path),
            fetchContent(afterOption, path),
          ]);

          if (cancelled) {
            return;
          }

          const beforeContent = beforeContentRaw.content;
          const afterContent = afterContentRaw.content;

          if (beforeContent === afterContent) {
            continue;
          }

          const entries = computeLineDiff(beforeContent, afterContent);
          if (!entries.some((entry) => entry.kind !== 'context')) {
            continue;
          }

          const stats = summarizeEntries(entries);
          addedTotal += stats.added;
          removedTotal += stats.removed;

          const rows = buildBaseRows(entries, path);
          const blocks = computeContextBlocks(rows, COLLAPSE_THRESHOLD, path);

          fileSummaries.push({
            path,
            entries,
            rows,
            blocks,
            stats,
            beforeExists: beforeContentRaw.found,
            afterExists: afterContentRaw.found,
          });
        }

        if (cancelled) {
          return;
        }

        fileSummaries.sort((a, b) => a.path.localeCompare(b.path, undefined, { sensitivity: 'base' }));

        setDiffFiles(fileSummaries);
        setTotalStats({ added: addedTotal, removed: removedTotal });
        setExpandedBlocks(new Set());
      } catch (err) {
        if (!cancelled) {
          console.error('[DiffPanel] Failed to compute diff:', err);
          setError('Unable to compute diff for the selected sources');
          setDiffFiles([]);
          setTotalStats({ added: 0, removed: 0 });
          setExpandedBlocks(new Set());
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadDiff();

    return () => {
      cancelled = true;
    };
  }, [afterOptionId, beforeOptionId, optionMap, currentSourcesRevision]);

  const diffFilesMap = useMemo(() => {
    const map = new Map<string, DiffFileData>();
    diffFiles.forEach((file) => {
      map.set(file.path, file);
    });
    return map;
  }, [diffFiles]);

  const explicitSelection = diffFilePathValue !== null;
  const fallbackPath = filePath && diffFilesMap.has(filePath) ? filePath : '';
  const activeSelectionKey = explicitSelection ? (diffFilePathValue ?? '') : fallbackPath;

  const selectedFiles = useMemo(() => {
    if (!diffFiles.length) {
      return [];
    }

    if (!activeSelectionKey || activeSelectionKey === '') {
      return diffFiles;
    }

    const directMatch = diffFilesMap.get(activeSelectionKey);
    if (directMatch) {
      return [directMatch];
    }

    const prefix = activeSelectionKey.endsWith('/') ? activeSelectionKey : `${activeSelectionKey}/`;
    return diffFiles.filter((file) => file.path.startsWith(prefix));
  }, [activeSelectionKey, diffFiles, diffFilesMap]);

  const sections = useMemo(() => {
    return selectedFiles.map((file) => ({
      file,
      rows: buildVisibleRows(file.rows, file.blocks, expandedBlocks, CONTEXT_MARGIN, COLLAPSE_THRESHOLD),
    }));
  }, [selectedFiles, expandedBlocks]);

  const diffCopyText = useMemo(() => {
    if (!beforeOption || !afterOption || !selectedFiles.length) {
      return '';
    }
    return buildUnifiedDiffString(beforeOption, afterOption, selectedFiles);
  }, [afterOption, beforeOption, selectedFiles]);

  const selectionAvailability = useMemo(() => {
    if (selectedFiles.length !== 1) {
      return null;
    }
    const [file] = selectedFiles;
    if (file.beforeExists && file.afterExists) {
      return null;
    }
    return {
      beforeFound: file.beforeExists,
      afterFound: file.afterExists,
    };
  }, [selectedFiles]);

  const diffTree = useMemo(() => buildDiffTree(diffFiles), [diffFiles]);

  useEffect(() => {
    if (diffFilePathValue === null || diffFilePathValue === '') {
      return;
    }

    const hasFile = diffFilesMap.has(diffFilePathValue);
    const folderPrefix = diffFilePathValue.endsWith('/') ? diffFilePathValue : `${diffFilePathValue}/`;
    const hasFolder = diffFiles.some((file) => file.path.startsWith(folderPrefix));

    if (!hasFile && !hasFolder) {
      select({ diff_file_path: '' });
    }
  }, [diffFilePathValue, diffFiles, diffFilesMap, select]);

  const setDiffPath = useCallback(
    (path: string) => {
      if (diffFilePathValue === path) {
        return;
      }
      select({ diff_file_path: path });
    },
    [diffFilePathValue, select],
  );

  const renderTreeNodes = (nodes: DiffTreeNode[], depth = 0): ReactNode[] => {
    return nodes.flatMap((node) => {
      const isActive = activeSelectionKey === node.key;
      const depthClass = `diff-nav__item--depth-${Math.min(depth, MAX_NAV_DEPTH_CLASS)}`;
      const label = node.isFolder ? `${node.name}/` : node.name;
      const item = (
        <button
          type="button"
          key={`diff-node-${node.key}`}
          className={`diff-nav__item ${depthClass}`}
          data-active={isActive}
          data-folder={node.isFolder}
          onClick={() => setDiffPath(node.key)}
        >
          <span className="diff-nav__label">{label}</span>
          <span className="diff-nav__metrics">
            {node.added ? <span className="diff-nav__delta diff-nav__delta--added">+{node.added}</span> : null}
            {node.removed ? <span className="diff-nav__delta diff-nav__delta--removed">-{node.removed}</span> : null}
          </span>
        </button>
      );
      const children = node.children.length ? renderTreeNodes(node.children, depth + 1) : [];
      return [item, ...children];
    });
  };

  const navItems = renderTreeNodes(diffTree.children);
  const isRootActive = activeSelectionKey === '';
  const totalFilesChanged = diffFiles.length;

  const handleToggleBlock = useCallback((blockId: string) => {
    setExpandedBlocks((prev) => {
      const next = new Set(prev);
      if (next.has(blockId)) {
        next.delete(blockId);
      } else {
        next.add(blockId);
      }
      return next;
    });
  }, []);

  const handleBeforeChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    const option = optionMap.get(value);
    setBeforeOptionId(option ? value : null);
    setExpandedBlocks(new Set());
    const versionId = option?.type === 'version' ? option.versionId ?? null : null;
    if (diffBeforeVersionId !== versionId) {
      select({ diff_before_version_id: versionId ?? null });
    }
  };

  const handleAfterChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const value = event.target.value;
    const option = optionMap.get(value);
    setAfterOptionId(option ? value : null);
    setExpandedBlocks(new Set());
    const versionId = option?.type === 'version' ? option.versionId ?? null : null;
    if (diffAfterVersionId !== versionId) {
      select({ diff_after_version_id: versionId ?? null });
    }
  };

  const handleViewModeChange = (mode: ViewMode) => {
    if (viewMode === mode) {
      return;
    }
    select({ diff_layout: mode });
  };

  const handleFileListToggle = useCallback((enabled: boolean) => {
    setShowFileList(enabled);
  }, []);

  const showNoOptions = !options.length;
  const showNoDiff = !loading && !error && !selectedFiles.length;

  let viewerContent: ReactNode;
  if (showNoOptions) {
    viewerContent = (
      <div className="empty-state">
        <p>No versions available</p>
        <p className="empty-state-subtitle">Create a snapshot to compare changes</p>
      </div>
    );
  } else if (loading) {
    viewerContent = (
      <div className="empty-state">
        <p>Loading diff...</p>
      </div>
    );
  } else if (error) {
    viewerContent = (
      <div className="empty-state">
        <p>{error}</p>
        <p className="empty-state-subtitle">Try choosing different versions or reload the panel</p>
      </div>
    );
  } else if (!beforeOption || !afterOption) {
    viewerContent = (
      <div className="empty-state">
        <p>Select versions to compare</p>
        <p className="empty-state-subtitle">Choose sources for both before and after</p>
      </div>
    );
  } else if (showNoDiff) {
    viewerContent = (
      <div className="empty-state">
        <p>No differences detected</p>
        <p className="empty-state-subtitle">Adjust the selection to see changes</p>
      </div>
    );
  } else {
    viewerContent = (
      <TextContentViewer
        content={diffCopyText}
        readOnly
        showLineNumbers={false}
        renderCustomContent={() => (
          <DiffContent sections={sections} viewMode={viewMode} onToggleBlock={handleToggleBlock} />
        )}
      />
    );
  }

  const selectionNotice = selectionAvailability && sections.length
    ? (
        <div className="diff-panel__notice">
          {!selectionAvailability.beforeFound && beforeOption ? (
            <p>File not found in {beforeOption.label}. Treating as empty.</p>
          ) : null}
          {!selectionAvailability.afterFound && afterOption ? (
            <p>File not found in {afterOption.label}. Treating as empty.</p>
          ) : null}
        </div>
      )
    : null;

  return (
    <div className="panel-shell">
      <div className="diff-panel">
        <div className="panel-controls">
          <select
            id={`${panelId}-diff-before`}
            className="input-surface diff-panel__select"
            value={beforeOptionId ?? ''}
            onChange={handleBeforeChange}
            disabled={showNoOptions}
            aria-label="Before version"
          >
            {options.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
          <select
            id={`${panelId}-diff-after`}
            className="input-surface diff-panel__select"
            value={afterOptionId ?? ''}
            onChange={handleAfterChange}
            disabled={showNoOptions}
            aria-label="After version"
          >
            {options.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
          <div className="diff-panel__toggle-group" role="group" aria-label="Diff view mode">
            <button
              type="button"
              className={`diff-panel__toggle${viewMode === 'inline' ? ' diff-panel__toggle--active' : ''}`}
              onClick={() => handleViewModeChange('inline')}
            >
              Inline
            </button>
            <button
              type="button"
              className={`diff-panel__toggle${viewMode === 'side-by-side' ? ' diff-panel__toggle--active' : ''}`}
              onClick={() => handleViewModeChange('side-by-side')}
            >
              Side by side
            </button>
          </div>
          <ToggleButton label="File list" enabled={showFileList} onToggle={handleFileListToggle} />
        </div>

        <div className="diff-panel__content">
          {showFileList ? (
            <aside className="diff-panel__nav">
              <div className="diff-nav__header">
                <span className="diff-nav__title">Files changed</span>
                <span className="diff-nav__count">{totalFilesChanged}</span>
              </div>
              <button
                type="button"
                className="diff-nav__item diff-nav__item--root diff-nav__item--depth-0"
                data-active={isRootActive}
                onClick={() => setDiffPath('')}
              >
                <span className="diff-nav__label">All changes</span>
                <span className="diff-nav__metrics">
                  <span className="diff-nav__delta diff-nav__delta--added">+{totalStats.added}</span>
                  <span className="diff-nav__delta diff-nav__delta--removed">-{totalStats.removed}</span>
                </span>
              </button>
              <div className="diff-nav__list">
                {navItems.length ? navItems : <p className="diff-nav__empty">No differences detected.</p>}
              </div>
            </aside>
          ) : null}
          <div className="diff-panel__viewer">
            {selectionNotice}
            {viewerContent}
          </div>
        </div>
      </div>
    </div>
  );
}

function DiffContent({ sections, viewMode, onToggleBlock }: DiffContentProps) {
  if (!sections.length) {
    return (
      <div className="diff-view diff-view--empty">
        <p className="diff-view__empty-text">No differences to display.</p>
      </div>
    );
  }

  return (
    <div className={`diff-view diff-view--${viewMode}`}>
      {sections.map(({ file, rows }) => {
        const status = !file.beforeExists && file.afterExists ? 'added' : file.beforeExists && !file.afterExists ? 'removed' : null;

        return (
          <Fragment key={file.path}>
            <div className="diff-view__file-header">
              <div className="diff-view__file-id">
                <span className="diff-view__file-name">{file.path}</span>
                {status === 'added' ? <span className="diff-view__badge diff-view__badge--positive">Added</span> : null}
                {status === 'removed' ? <span className="diff-view__badge diff-view__badge--critical">Deleted</span> : null}
              </div>
              <div className="diff-view__file-metrics">
                <span className="diff-view__delta diff-view__delta--added">+{file.stats.added}</span>
                <span className="diff-view__delta diff-view__delta--removed">-{file.stats.removed}</span>
              </div>
            </div>
            {rows.length ? (
              viewMode === 'inline'
                ? renderInlineRows(rows, onToggleBlock)
                : rows.map((row) => {
                    if (row.type === 'collapse') {
                      const { hiddenCount, beforeRange, afterRange, blockId } = row.info;
                      const beforeLabel = formatRange(beforeRange);
                      const afterLabel = formatRange(afterRange);
                      const rangeSegment = [beforeLabel ? `before ${beforeLabel}` : null, afterLabel ? `after ${afterLabel}` : null]
                        .filter(Boolean)
                        .join(' · ');
                      return (
                        <div key={row.key} className="diff-view__collapse">
                          <span className="diff-view__collapse-text">
                            Skipped {hiddenCount} unchanged lines{rangeSegment ? ` (${rangeSegment})` : ''}
                          </span>
                          <button type="button" className="diff-view__collapse-button" onClick={() => onToggleBlock(blockId)}>
                            Expand
                          </button>
                        </div>
                      );
                    }

                    const left = row.row.left;
                    const right = row.row.right;
                    return (
                      <div key={row.key} className="diff-view__line diff-view__line--side-by-side">
                        <span className={`diff-line__number diff-line__number--before diff-line__number--${left.kind}`}>
                          {left.lineNumber ?? ''}
                        </span>
                        <span className={`diff-line__content diff-line__content--before diff-line__content--${left.kind}`}>
                          {left.text === '' ? '\u00A0' : left.text}
                        </span>
                        <span className={`diff-line__number diff-line__number--after diff-line__number--${right.kind}`}>
                          {right.lineNumber ?? ''}
                        </span>
                        <span className={`diff-line__content diff-line__content--after diff-line__content--${right.kind}`}>
                          {right.text === '' ? '\u00A0' : right.text}
                        </span>
                      </div>
                    );
                  })
            ) : (
              <div className="diff-view__empty-section">
                <p className="diff-view__empty-text">No visible changes in this scope.</p>
              </div>
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

function renderInlineRows(rows: VisibleRow[], onToggleBlock: (blockId: string) => void): ReactNode[] {
  type QueuedSegment = { segment: InlineSegment; rowKey: string };

  const elements: ReactNode[] = [];
  let pendingRemoved: QueuedSegment[] = [];
  let pendingAdded: QueuedSegment[] = [];
  let currentChangeGroupId: string | null = null;
  let sequence = 0;

  const flushPending = () => {
    if (!pendingRemoved.length && !pendingAdded.length) {
      return;
    }
    pendingRemoved.forEach(({ segment, rowKey }) => {
      elements.push(renderInlineLine(segment, `${rowKey}-removed-${sequence}`));
      sequence += 1;
    });
    pendingAdded.forEach(({ segment, rowKey }) => {
      elements.push(renderInlineLine(segment, `${rowKey}-added-${sequence}`));
      sequence += 1;
    });
    pendingRemoved = [];
    pendingAdded = [];
    currentChangeGroupId = null;
  };

  rows.forEach((row) => {
    if (row.type === 'collapse') {
      flushPending();
      const { hiddenCount, beforeRange, afterRange, blockId } = row.info;
      const beforeLabel = formatRange(beforeRange);
      const afterLabel = formatRange(afterRange);
      const rangeSegment = [beforeLabel ? `before ${beforeLabel}` : null, afterLabel ? `after ${afterLabel}` : null]
        .filter(Boolean)
        .join(' · ');
      elements.push(
        <div key={row.key} className="diff-view__collapse">
          <span className="diff-view__collapse-text">
            Skipped {hiddenCount} unchanged lines{rangeSegment ? ` (${rangeSegment})` : ''}
          </span>
          <button type="button" className="diff-view__collapse-button" onClick={() => onToggleBlock(blockId)}>
            Expand
          </button>
        </div>,
      );
      return;
    }

    const segments = renderInlineRow(row.row).map<QueuedSegment>((segment) => ({
      segment,
      rowKey: row.key,
    }));

    if (segments.length === 1 && segments[0].segment.kind === 'context') {
      flushPending();
      elements.push(renderInlineLine(segments[0].segment, `${row.key}-context`));
      return;
    }

    const changeGroupId = row.key.includes('::change-') ? row.key.slice(0, row.key.lastIndexOf('-')) : row.key;
    if (currentChangeGroupId !== null && changeGroupId !== currentChangeGroupId) {
      flushPending();
    }
    currentChangeGroupId = changeGroupId;

    segments.forEach(({ segment, rowKey }) => {
      if (segment.kind === 'removed') {
        pendingRemoved.push({ segment, rowKey });
      } else if (segment.kind === 'added') {
        pendingAdded.push({ segment, rowKey });
      }
    });
  });

  flushPending();

  return elements;
}

function renderInlineLine(segment: InlineSegment, key: string): ReactNode {
  return (
    <div key={key} className={`diff-view__line diff-view__line--inline diff-line--${segment.kind}`}>
      <span className="diff-line__number diff-line__number--before">{segment.before ?? ''}</span>
      <span className="diff-line__number diff-line__number--after">{segment.after ?? ''}</span>
      <span className="diff-line__marker">{segment.marker}</span>
      <span className="diff-line__content">{segment.text === '' ? '\u00A0' : segment.text}</span>
    </div>
  );
}

function renderInlineRow(row: BaseRow): InlineSegment[] {
  const segments: InlineSegment[] = [];

  if (row.left.kind === 'context' && row.right.kind === 'context') {
    segments.push({
      key: 'context',
      kind: 'context',
      before: row.left.lineNumber,
      after: row.right.lineNumber,
      marker: ' ',
      text: row.left.text,
    });
    return segments;
  }

  if (row.left.kind !== 'blank') {
    segments.push({
      key: 'left',
      kind: row.left.kind,
      before: row.left.lineNumber,
      after: null,
      marker: row.left.kind === 'removed' ? '-' : ' ',
      text: row.left.text,
    });
  }

  if (row.right.kind !== 'blank') {
    segments.push({
      key: 'right',
      kind: row.right.kind,
      before: null,
      after: row.right.lineNumber,
      marker: row.right.kind === 'added' ? '+' : ' ',
      text: row.right.text,
    });
  }

  return segments;
}

function formatRange(range: RangeInfo): string | null {
  if (range.start === null || range.end === null) {
    return null;
  }
  if (range.start === range.end) {
    return `line ${range.start}`;
  }
  return `lines ${range.start}–${range.end}`;
}

async function fetchContent(option: DiffSourceOption, path: string): Promise<{ content: string; found: boolean }> {
  try {
    if (option.type === 'current') {
      const content = await api().services.files.read(path);
      if (typeof content === 'string') {
        return { content, found: true };
      }
      return { content: '', found: false };
    }
    if (option.versionId === undefined) {
      return { content: '', found: false };
    }
    const content = await api().services.files.read(path, option.versionId);
    if (typeof content === 'string') {
      return { content, found: true };
    }
    return { content: '', found: false };
  } catch (err) {
    console.error('[DiffPanel] Failed to fetch file content:', err);
    return { content: '', found: false };
  }
}

function buildBaseRows(entries: DiffEntry[], fileKey: string): BaseRow[] {
  const rows: BaseRow[] = [];
  const removedBuffer: DiffEntry[] = [];
  const addedBuffer: DiffEntry[] = [];
  let changeBlockCounter = 0;

  const flushBuffers = () => {
    if (!removedBuffer.length && !addedBuffer.length) {
      return;
    }
    const total = Math.max(removedBuffer.length, addedBuffer.length);
    const blockId = changeBlockCounter;
    changeBlockCounter += 1;
    for (let index = 0; index < total; index += 1) {
      const removed = removedBuffer[index] ?? null;
      const added = addedBuffer[index] ?? null;
      rows.push({
        key: `${fileKey}::change-${blockId}-${index}`,
        left: removed
          ? { kind: 'removed', lineNumber: removed.beforeLine, text: removed.text }
          : { kind: 'blank', lineNumber: null, text: '' },
        right: added
          ? { kind: 'added', lineNumber: added.afterLine, text: added.text }
          : { kind: 'blank', lineNumber: null, text: '' },
      });
    }
    removedBuffer.length = 0;
    addedBuffer.length = 0;
  };

  entries.forEach((entry, index) => {
    if (entry.kind === 'context') {
      flushBuffers();
      rows.push({
        key: `${fileKey}::context-${index}`,
        left: { kind: 'context', lineNumber: entry.beforeLine, text: entry.text },
        right: { kind: 'context', lineNumber: entry.afterLine, text: entry.text },
      });
      return;
    }
    if (entry.kind === 'removed') {
      removedBuffer.push(entry);
      return;
    }
    addedBuffer.push(entry);
  });

  flushBuffers();
  return rows;
}

function computeContextBlocks(rows: BaseRow[], threshold: number, keyPrefix: string): ContextBlock[] {
  const blocks: ContextBlock[] = [];
  let start: number | null = null;

  for (let index = 0; index < rows.length; index += 1) {
    const row = rows[index];
    const isContext = row.left.kind === 'context' && row.right.kind === 'context';
    if (isContext) {
      if (start === null) {
        start = index;
      }
      continue;
    }
    if (start !== null) {
      const length = index - start;
      if (length > threshold) {
        blocks.push({ id: `${keyPrefix}::block-${blocks.length}`, start, end: index - 1 });
      }
      start = null;
    }
  }

  if (start !== null) {
    const length = rows.length - start;
    if (length > threshold) {
      blocks.push({ id: `${keyPrefix}::block-${blocks.length}`, start, end: rows.length - 1 });
    }
  }

  return blocks;
}

function buildVisibleRows(
  rows: BaseRow[],
  blocks: ContextBlock[],
  expandedBlocks: Set<string>,
  margin: number,
  threshold: number,
): VisibleRow[] {
  if (!rows.length) {
    return [];
  }

  const visible: VisibleRow[] = [];
  const indexToBlock = new Map<number, ContextBlock>();
  blocks.forEach((block) => {
    for (let index = block.start; index <= block.end; index += 1) {
      indexToBlock.set(index, block);
    }
  });

  let index = 0;
  while (index < rows.length) {
    const block = indexToBlock.get(index);
    if (!block) {
      visible.push({ type: 'line', key: rows[index].key, row: rows[index] });
      index += 1;
      continue;
    }

    const blockLength = block.end - block.start + 1;
    if (blockLength <= threshold || expandedBlocks.has(block.id)) {
      for (let idx = block.start; idx <= block.end; idx += 1) {
        visible.push({ type: 'line', key: rows[idx].key, row: rows[idx] });
      }
      index = block.end + 1;
      continue;
    }

    const prefixCount = Math.min(margin, blockLength);
    const suffixCount = Math.min(margin, blockLength - prefixCount);
    const hiddenCount = blockLength - prefixCount - suffixCount;

    if (hiddenCount <= 0) {
      for (let idx = block.start; idx <= block.end; idx += 1) {
        visible.push({ type: 'line', key: rows[idx].key, row: rows[idx] });
      }
      index = block.end + 1;
      continue;
    }

    for (let idx = block.start; idx < block.start + prefixCount; idx += 1) {
      visible.push({ type: 'line', key: rows[idx].key, row: rows[idx] });
    }

    const hiddenStartIndex = block.start + prefixCount;
    const hiddenEndIndex = block.end - suffixCount;
    const hiddenStartRow = rows[hiddenStartIndex];
    const hiddenEndRow = rows[hiddenEndIndex];

    visible.push({
      type: 'collapse',
      key: `${block.id}-collapse-${hiddenStartRow.left.lineNumber ?? hiddenStartRow.right.lineNumber ?? 0}`,
      info: {
        blockId: block.id,
        hiddenCount,
        beforeRange: {
          start: hiddenStartRow.left.lineNumber,
          end: hiddenEndRow.left.lineNumber,
        },
        afterRange: {
          start: hiddenStartRow.right.lineNumber,
          end: hiddenEndRow.right.lineNumber,
        },
      },
    });

    for (let idx = block.end - suffixCount + 1; idx <= block.end; idx += 1) {
      visible.push({ type: 'line', key: rows[idx].key, row: rows[idx] });
    }

    index = block.end + 1;
  }

  return visible;
}

function buildUnifiedDiffString(
  beforeOption: DiffSourceOption,
  afterOption: DiffSourceOption,
  files: DiffFileData[],
): string {
  if (!files.length) {
    return '';
  }

  const lines: string[] = [];
  files.forEach((file, index) => {
    lines.push(`--- ${beforeOption.label} · ${file.path}`);
    lines.push(`+++ ${afterOption.label} · ${file.path}`);
    file.entries.forEach((entry) => {
      switch (entry.kind) {
        case 'context':
          lines.push(` ${entry.text}`);
          break;
        case 'removed':
          lines.push(`-${entry.text}`);
          break;
        case 'added':
          lines.push(`+${entry.text}`);
          break;
        default:
          break;
      }
    });
    if (index < files.length - 1) {
      lines.push('');
    }
  });
  return lines.join('\n');
}

function summarizeEntries(entries: DiffEntry[]): DiffStats {
  let added = 0;
  let removed = 0;

  entries.forEach((entry) => {
    if (entry.kind === 'added') {
      added += 1;
    } else if (entry.kind === 'removed') {
      removed += 1;
    }
  });

  return { added, removed };
}

async function fetchFileList(option: DiffSourceOption): Promise<string[]> {
  try {
    if (option.type === 'current') {
      const list = await api().services.files.list();
      return Array.isArray(list) ? list : [];
    }
    if (option.versionId === undefined) {
      return [];
    }
    const list = await api().services.files.list(option.versionId);
    return Array.isArray(list) ? list : [];
  } catch (err) {
    console.error('[DiffPanel] Failed to list files:', err);
    return [];
  }
}

function buildDiffTree(files: DiffFileData[]): DiffTreeNode {
  const root: DiffTreeNode = {
    key: '',
    name: '',
    isFolder: true,
    children: [],
    added: 0,
    removed: 0,
  };

  const ensureChild = (parent: DiffTreeNode, name: string, key: string, isFolder: boolean): DiffTreeNode => {
    let child = parent.children.find((node) => node.key === key && node.isFolder === isFolder);
    if (!child) {
      child = {
        key,
        name,
        isFolder,
        children: [],
        added: 0,
        removed: 0,
      };
      parent.children.push(child);
    }
    return child;
  };

  files.forEach((file) => {
    root.added += file.stats.added;
    root.removed += file.stats.removed;

    const parts = file.path.split('/');
    let current = root;
    let currentPath = '';

    parts.forEach((part, index) => {
      const isLast = index === parts.length - 1;
      currentPath = currentPath ? `${currentPath}/${part}` : part;
      if (isLast) {
        const fileNode = ensureChild(current, part, currentPath, false);
        fileNode.added += file.stats.added;
        fileNode.removed += file.stats.removed;
      } else {
        current = ensureChild(current, part, currentPath, true);
        current.added += file.stats.added;
        current.removed += file.stats.removed;
      }
    });
  });

  const sortNodes = (node: DiffTreeNode) => {
    node.children.sort((a, b) => {
      if (a.isFolder !== b.isFolder) {
        return a.isFolder ? -1 : 1;
      }
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });
    node.children.forEach(sortNodes);
  };

  sortNodes(root);
  return root;
}
